'use client';
import React, { useState, useEffect } from 'react';
import { useRouter, useParams } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Search, ChevronDown } from 'lucide-react';
import { Template, TemplateItem } from '@/lib/types';
import { getTemplateById, upsertTemplate, generateTemplateId } from '@/lib/storage';
import { CATALOGUE, VALID_UNITS, VALID_GST_RATES } from '@/lib/catalogue';
import ToastContainer, { showToast } from '@/components/ui/Toast';

function createEmptyItem(): TemplateItem {
  return {
    description: '',
    hsn: '',
    qty: 1,
    unit: 'Nos',
    rate: 0,
    gstRate: 18,
  };
}

export default function TemplateEditorPage() {
  const router = useRouter();
  const params = useParams();
  const templateId = params.id as string;
  const isNew = templateId === 'new';

  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState<TemplateItem[]>([createEmptyItem()]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [catalogueSearch, setCatalogueSearch] = useState<Record<number, string>>({});
  const [showCatalogue, setShowCatalogue] = useState<number | null>(null);

  useEffect(() => {
    if (!isNew) {
      const template = getTemplateById(templateId);
      if (template) {
        setName(template.name);
        setDescription(template.description);
        setItems(template.items.length > 0 ? template.items : [createEmptyItem()]);
      } else {
        showToast('Template not found', 'error');
        router.push('/templates');
      }
    }
  }, [templateId, isNew, router]);

  const updateItem = (index: number, field: keyof TemplateItem, value: string | number) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-set GST rate for plants (HSN 0602)
      if (field === 'hsn' && String(value) === '0602') {
        updated[index].gstRate = 0;
      }
      return updated;
    });
  };

  const handleCatalogueSelect = (index: number, catalogueId: string) => {
    const cat = CATALOGUE.find((c) => c.id === catalogueId);
    if (!cat) return;
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        description: cat.description,
        hsn: cat.hsn,
        gstRate: cat.gstRate,
        unit: cat.defaultUnit,
      };
      return updated;
    });
    setShowCatalogue(null);
    setCatalogueSearch((prev) => ({ ...prev, [index]: '' }));
  };

  const addItem = () => {
    setItems((prev) => [...prev, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) {
      showToast('Template must have at least one item', 'error');
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const moveItem = (fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= items.length) return;
    setItems((prev) => {
      const updated = [...prev];
      [updated[fromIndex], updated[toIndex]] = [updated[toIndex], updated[fromIndex]];
      return updated;
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Template name is required';
    items.forEach((item, i) => {
      if (!item.description.trim()) errs[`item-${i}-desc`] = 'Description required';
      if (item.qty <= 0) errs[`item-${i}-qty`] = 'Qty must be > 0';
      if (item.rate <= 0) errs[`item-${i}-rate`] = 'Rate must be > 0';
    });
    if (items.length === 0) errs.items = 'At least one item is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showToast('Please fix the errors before saving', 'error');
      return;
    }

    setSaving(true);
    await new Promise((r) => setTimeout(r, 300));

    const template: Template = {
      id: isNew ? generateTemplateId() : templateId,
      name: name.trim(),
      description: description.trim(),
      items,
      isDefault: false,
    };

    if (!isNew) {
      const existing = getTemplateById(templateId);
      if (existing) {
        template.isDefault = existing.isDefault;
        template.createdAt = existing.createdAt;
      }
    }

    upsertTemplate(template);
    setSaving(false);
    showToast(`Template "${template.name}" saved`, 'success');
    router.push('/templates');
  };

  const filteredCatalogue = (index: number) => {
    const search = (catalogueSearch[index] || '').toLowerCase();
    if (!search) return CATALOGUE.slice(0, 10);
    return CATALOGUE.filter(
      (c) =>
        c.description.toLowerCase().includes(search) ||
        c.hsn.includes(search) ||
        c.category.toLowerCase().includes(search)
    ).slice(0, 10);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between gap-4 flex-wrap sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Link
            href="/templates"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          >
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-lg font-bold text-foreground">
              {isNew ? 'New Template' : 'Edit Template'}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isNew ? 'Create a reusable line item template' : `Editing: ${name || 'Untitled'}`}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary disabled:opacity-60"
        >
          {saving ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save size={16} />
          )}
          {saving ? 'Saving...' : 'Save Template'}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-background">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Template Info */}
          <div className="form-section">
            <h2 className="form-section-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="21" x2="9" y2="9"/>
              </svg>
              Template Details
            </h2>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="form-label">
                  Template Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Basic Terrace Garden (100 sqft)"
                  className={`form-input ${errors.name ? 'border-destructive' : ''}`}
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of what this template includes..."
                  rows={2}
                  className="form-input resize-none"
                />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="form-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="form-section-title mb-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
                  <line x1="8" y1="6" x2="21" y2="6"/>
                  <line x1="8" y1="12" x2="21" y2="12"/>
                  <line x1="8" y1="18" x2="21" y2="18"/>
                  <line x1="3" y1="6" x2="3.01" y2="6"/>
                  <line x1="3" y1="12" x2="3.01" y2="12"/>
                  <line x1="3" y1="18" x2="3.01" y2="18"/>
                </svg>
                Line Items
                <span className="ml-2 text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {items.length} item{items.length !== 1 ? 's' : ''}
                </span>
              </h2>
              <button onClick={addItem} className="btn-secondary text-xs py-1.5">
                <Plus size={14} />
                Add Item
              </button>
            </div>

            {errors.items && <p className="text-sm text-destructive mb-4">{errors.items}</p>}

            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="bg-muted/30 border border-border rounded-xl p-4 relative group"
                >
                  {/* Row number & move buttons */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === items.length - 1}
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    </div>
                    <div className="flex-1" />
                    <button
                      onClick={() => removeItem(index)}
                      className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title="Remove item"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Description with catalogue search */}
                  <div className="mb-3 relative">
                    <label className="form-label text-xs">Description *</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        placeholder="Item description"
                        className={`form-input pr-10 ${errors[`item-${index}-desc`] ? 'border-destructive' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCatalogue(showCatalogue === index ? null : index)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                        title="Search catalogue"
                      >
                        <Search size={14} />
                      </button>
                    </div>
                    {errors[`item-${index}-desc`] && (
                      <p className="text-xs text-destructive mt-1">{errors[`item-${index}-desc`]}</p>
                    )}

                    {/* Catalogue dropdown */}
                    {showCatalogue === index && (
                      <div className="absolute z-20 mt-1 w-full bg-card border border-border rounded-xl shadow-lg max-h-64 overflow-auto">
                        <div className="p-2 border-b border-border sticky top-0 bg-card">
                          <input
                            type="text"
                            value={catalogueSearch[index] || ''}
                            onChange={(e) => setCatalogueSearch((prev) => ({ ...prev, [index]: e.target.value }))}
                            placeholder="Search catalogue..."
                            className="form-input text-sm py-1.5"
                            autoFocus
                          />
                        </div>
                        <div className="p-1">
                          {filteredCatalogue(index).map((cat) => (
                            <button
                              key={cat.id}
                              onClick={() => handleCatalogueSelect(index, cat.id)}
                              className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors"
                            >
                              <div className="font-medium text-foreground truncate">{cat.description}</div>
                              <div className="text-xs text-muted-foreground">
                                HSN: {cat.hsn} | GST: {cat.gstRate}% | {cat.category}
                              </div>
                            </button>
                          ))}
                          {filteredCatalogue(index).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">No items found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Other fields */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div>
                      <label className="form-label text-xs">HSN/SAC</label>
                      <input
                        type="text"
                        value={item.hsn}
                        onChange={(e) => updateItem(index, 'hsn', e.target.value)}
                        placeholder="HSN"
                        className="form-input text-sm"
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Qty *</label>
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateItem(index, 'qty', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className={`form-input text-sm ${errors[`item-${index}-qty`] ? 'border-destructive' : ''}`}
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">Unit</label>
                      <div className="relative">
                        <select
                          value={item.unit}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                          className="form-select text-sm"
                        >
                          {VALID_UNITS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="form-label text-xs">Rate (₹) *</label>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className={`form-input text-sm ${errors[`item-${index}-rate`] ? 'border-destructive' : ''}`}
                      />
                    </div>
                    <div>
                      <label className="form-label text-xs">GST %</label>
                      <div className="relative">
                        <select
                          value={item.gstRate}
                          onChange={(e) => updateItem(index, 'gstRate', parseFloat(e.target.value))}
                          className="form-select text-sm"
                        >
                          {VALID_GST_RATES.map((r) => (
                            <option key={r} value={r}>{r}%</option>
                          ))}
                        </select>
                        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addItem}
              className="w-full mt-4 py-3 border-2 border-dashed border-border rounded-xl text-sm text-muted-foreground hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
            >
              <Plus size={16} />
              Add Another Item
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
